import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch FE Base list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeCapitalContributionMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Instead of primitive values, use objects to hold value, error status and helper text
  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    paid_capital: { value: '', error: false, helperText: '' },
    shares_debentures: { value: '', error: false, helperText: '' },
  });

  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'paid_capital', field: 'paid_capital', headerName: 'Paid Capital', width: 150 },
    { id: 'shares_debentures', field: 'shares_debentures', headerName: 'Shares/Debentures', width: 300 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchCapitalContributions = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-capital-contribution/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            paid_capital: item.paid_capital,
            shares_debentures: item.shares_debentures,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch Capital Contributions:', error);
        setSnackbarMessage('Failed to load Capital Contributions.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchCapitalContributions();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  // Reset form fields to empty with no errors
  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      paid_capital: { value: '', error: false, helperText: '' },
      shares_debentures: { value: '', error: false, helperText: '' },
    });
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  // On edit, set form fields value + reset errors; find FE Base id by name or id for form value consistency
  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      paid_capital: { value: item.paid_capital, error: false, helperText: '' },
      shares_debentures: { value: item.shares_debentures, error: false, helperText: '' },
    });
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-capital-contribution/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Capital Contribution deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchCapitalContributions();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Capital Contribution.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  // Validate individual field and return error message or empty string
  const validateField = (name, value) => {
    if (!value.trim()) {
      if (name === 'financial_establishment') return 'FE Base is required';
      if (name === 'paid_capital') return 'Paid Capital is required';
      if (name === 'shares_debentures') return 'Shares/Debentures is required';
    }
    return '';
  };

  // Handle input changes on form fields updating value + validation error/helperText
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: {
        value,
        error: !!errorMessage,
        helperText: errorMessage,
      },
    }));
  };

  // Validate all fields in form and update error states
  const validateForm = () => {
    let isValid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach(field => {
      const errorMessage = validateField(field, newFields[field].value);
      newFields[field] = {
        ...newFields[field],
        error: !!errorMessage,
        helperText: errorMessage,
      };
      if (errorMessage) isValid = false;
    });
    setFormFields(newFields);
    return isValid;
  };

  // Handle form submission with validation and API calls
  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      financial_establishment: formFields.financial_establishment.value,
      paid_capital: formFields.paid_capital.value,
      shares_debentures: formFields.shares_debentures.value,
    };

    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-capital-contribution/${selectedItem.id}/`, payload);
        setSnackbarMessage('Capital Contribution updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-capital-contribution/`, payload);
        setSnackbarMessage('Capital Contribution added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchCapitalContributions();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Capital Contribution.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', bgcolor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Capital Contributions
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent 
        columns={columns} 
        rows={rows} 
        manageColumnStatus 
        enableSort 
        sx={{ '& .MuiDataGrid-columnHeaders': { bgcolor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Capital Contribution' : 'Add Capital Contribution'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            name="financial_establishment"
            value={formFields.financial_establishment.value}
            error={formFields.financial_establishment.error}
            helperText={formFields.financial_establishment.helperText}
            onChange={handleInputChange}
            disabled={uploading}
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>
                {fe.name_of_fe || fe.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Paid Capital"
            fullWidth
            name="paid_capital"
            value={formFields.paid_capital.value}
            error={formFields.paid_capital.error}
            helperText={formFields.paid_capital.helperText}
            onChange={handleInputChange}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Shares/Debentures"
            fullWidth
            multiline
            minRows={3}
            name="shares_debentures"
            value={formFields.shares_debentures.value}
            error={formFields.shares_debentures.error}
            helperText={formFields.shares_debentures.helperText}
            onChange={handleInputChange}
            disabled={uploading}
          />
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                Processing...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Capital Contribution?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent 
        open={snackbarOpen} 
        message={snackbarMessage} 
        severity={snackbarSeverity} 
        onClose={handleSnackbarClose} 
      />
    </Box>
  );
};
